import {
  AccountVariable,
  AccountVariableType,
  ItContactValue,
} from '@/client/accounts';
import {
  POINT_OF_CONTACT_FORMAT,
  POINT_OF_CONTACT_TYPE_ATTRIBUTE,
} from '@/utils/helpers/point-of-contact.helper';
import {
  addCaretToTooltip,
  createTooltipContainer,
  getTargetRectViewportForElement,
  isInsideSummaryContainer,
  positionAndStyleTooltip,
} from '@/utils/helpers/tooltip-dom.helper';
import { t } from 'i18next';
import type React from 'react';

const POINT_OF_CONTACT_RESOLVED_CLASS = 'point-of-contact--resolved';
const HOVER_TOOLTIP_ID = 'course-content-tooltip';
const QUILL_TOOLTIP_SELECTOR = '.custom-tooltip[data-quill-tooltip]';
export const POINT_OF_CONTACT_TOOLTIP_CLASSES = [
  'white',
  'point-of-contact-tooltip-container',
];

type PointOfContactRow = {
  field: keyof Omit<ItContactValue, 'name'>;
  icon: string;
  labelKey: string;
  copyable: boolean;
};

const POINT_OF_CONTACT_ROWS: PointOfContactRow[] = [
  {
    field: 'email',
    icon: 'pi pi-envelope',
    labelKey: 'course.pointOfContact.tooltip.email',
    copyable: true,
  },
  {
    field: 'phone',
    icon: 'pi pi-phone',
    labelKey: 'course.pointOfContact.tooltip.phone',
    copyable: true,
  },
  {
    field: 'openingHours',
    icon: 'pi pi-clock',
    labelKey: 'course.pointOfContact.tooltip.openingHours',
    copyable: false,
  },
  {
    field: 'other',
    icon: 'pi pi-align-left',
    labelKey: 'course.pointOfContact.tooltip.other',
    copyable: false,
  },
];

const isPointOfContactType = (value: string): value is AccountVariableType =>
  Object.values(AccountVariableType).includes(value as AccountVariableType);

export const resolvePointOfContact = (
  accountVariables: AccountVariable[] | undefined,
  type: string | null,
): ItContactValue | null => {
  if (!type || !isPointOfContactType(type)) return null;

  const value = accountVariables?.find(
    (variable) => variable.type === type,
  )?.value;

  return value?.name?.trim() ? value : null;
};

const createPointOfContactIcon = (
  icon: string,
  label?: string,
): HTMLElement => {
  const element = document.createElement('i');
  element.className = `point-of-contact-tooltip-icon ${icon}`;
  if (label) {
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', label);
  } else {
    element.setAttribute('aria-hidden', 'true');
  }
  return element;
};

const createPointOfContactCopyButton = (
  value: string,
  onCopied?: () => void,
): HTMLButtonElement => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'point-of-contact-tooltip-copy';
  button.setAttribute(
    'aria-label',
    t('course.pointOfContact.tooltip.copyToClipboard'),
  );

  const icon = document.createElement('i');
  icon.className = 'pi pi-copy';
  icon.setAttribute('aria-hidden', 'true');
  button.appendChild(icon);

  const copy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => onCopied?.())
      .catch(() => undefined);
  };

  button.addEventListener('mousedown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    copy();
  });

  // detail === 0 marks a click synthesised by Enter or Space, so a pointer
  // click cannot reach here and copy a second time.
  button.addEventListener('click', (e) => {
    if (e.detail !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    copy();
  });

  return button;
};

// Every value goes in through textContent: these strings are admin-supplied.
export const buildPointOfContactContent = (
  contact: ItContactValue,
  onCopied?: () => void,
): HTMLElement => {
  const card = document.createElement('div');
  card.className = 'point-of-contact-tooltip';

  const headline = document.createElement('div');
  headline.className = 'point-of-contact-tooltip-headline';
  headline.appendChild(createPointOfContactIcon('pi pi-user'));

  const name = document.createElement('span');
  name.textContent = contact.name.trim();
  headline.appendChild(name);
  card.appendChild(headline);

  POINT_OF_CONTACT_ROWS.forEach(({ field, icon, labelKey, copyable }) => {
    const value = contact[field]?.trim();
    if (!value) return;

    const row = document.createElement('div');
    row.className = 'point-of-contact-tooltip-row';
    row.appendChild(createPointOfContactIcon(icon, t(labelKey)));

    const text = document.createElement('span');
    text.className = 'point-of-contact-tooltip-value';
    text.textContent = value;
    row.appendChild(text);

    if (copyable) {
      row.appendChild(createPointOfContactCopyButton(value, onCopied));
    }

    card.appendChild(row);
  });

  return card;
};

export const setupTooltipHoverForRenderer = (
  containerRef: React.RefObject<HTMLElement>,
  rtl = false,
  accountVariables?: AccountVariable[],
  onCopied?: () => void,
) => {
  const container = containerRef.current;
  if (!container) {
    return () => {
      // No cleanup needed if container is not available
    };
  }

  let activeTooltip: { teardown: () => void } | null = null;

  const teardownActiveTooltip = () => {
    activeTooltip?.teardown();
    activeTooltip = null;
  };

  const showHoverTooltip = (
    target: HTMLElement,
    content: HTMLElement | string,
    variantClassNames: string[] = [],
    isInteractive = false,
  ) => {
    teardownActiveTooltip();

    const isInSummary = isInsideSummaryContainer(target);
    const hoverTooltip = createTooltipContainer(
      'custom-tooltip-hover-display',
      isInSummary,
    );
    if (variantClassNames.length > 0) {
      hoverTooltip.classList.add(...variantClassNames);
    }
    if (typeof content === 'string') {
      hoverTooltip.textContent = content;
    } else {
      hoverTooltip.appendChild(content);
    }
    if (rtl) {
      hoverTooltip.setAttribute('dir', 'rtl');
    }
    hoverTooltip.id = HOVER_TOOLTIP_ID;
    if (!isInteractive) {
      hoverTooltip.setAttribute('role', 'tooltip');
    }
    addCaretToTooltip(hoverTooltip);
    document.body.appendChild(hoverTooltip);
    target.setAttribute('aria-describedby', HOVER_TOOLTIP_ID);

    const targetRectViewport = getTargetRectViewportForElement(target);
    positionAndStyleTooltip(hoverTooltip, targetRectViewport);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const teardown = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      hoverTooltip.remove();
      target.removeAttribute('aria-describedby');
      target.removeEventListener('mouseleave', scheduleRemove);
      target.removeEventListener('blur', scheduleRemove);
      target.removeEventListener('keydown', handleEscape);
      hoverTooltip.removeEventListener('mouseenter', cancelRemove);
      hoverTooltip.removeEventListener('mouseleave', scheduleRemove);
      hoverTooltip.removeEventListener('focusin', cancelRemove);
      hoverTooltip.removeEventListener('focusout', scheduleRemove);
    };

    const scheduleRemove = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(teardownActiveTooltip, 100);
    };

    const cancelRemove = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      teardownActiveTooltip();
    };

    target.addEventListener('mouseleave', scheduleRemove);
    target.addEventListener('blur', scheduleRemove);
    target.addEventListener('keydown', handleEscape);
    hoverTooltip.addEventListener('mouseenter', cancelRemove);
    hoverTooltip.addEventListener('mouseleave', scheduleRemove);
    hoverTooltip.addEventListener('focusin', cancelRemove);
    hoverTooltip.addEventListener('focusout', scheduleRemove);

    activeTooltip = { teardown };
  };

  const markPointsOfContact = () => {
    const markers = container.querySelectorAll<HTMLElement>(
      `.${POINT_OF_CONTACT_FORMAT}[${POINT_OF_CONTACT_TYPE_ATTRIBUTE}]`,
    );

    markers.forEach((marker) => {
      const contact = resolvePointOfContact(
        accountVariables,
        marker.getAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE),
      );
      marker.classList.toggle(POINT_OF_CONTACT_RESOLVED_CLASS, !!contact);
      if (contact) {
        marker.tabIndex = 0;
      } else {
        marker.removeAttribute('tabindex');
      }
    });

    container
      .querySelectorAll<HTMLElement>(QUILL_TOOLTIP_SELECTOR)
      .forEach((trigger) => {
        trigger.tabIndex = 0;
      });
  };

  const handleTooltipHover = (e: Event) => {
    const target = e.target as HTMLElement;
    if (!target?.closest) return;

    const pointOfContact = target.closest<HTMLElement>(
      `.${POINT_OF_CONTACT_RESOLVED_CLASS}[${POINT_OF_CONTACT_TYPE_ATTRIBUTE}]`,
    );
    if (pointOfContact && container.contains(pointOfContact)) {
      const contact = resolvePointOfContact(
        accountVariables,
        pointOfContact.getAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE),
      );
      if (!contact) return;
      showHoverTooltip(
        pointOfContact,
        buildPointOfContactContent(contact, onCopied),
        POINT_OF_CONTACT_TOOLTIP_CLASSES,
        true,
      );
      return;
    }

    if (
      target.classList.contains('custom-tooltip') &&
      target.hasAttribute('data-quill-tooltip')
    ) {
      const tooltipText = target.getAttribute('data-quill-tooltip') || '';
      if (!tooltipText) return;
      showHoverTooltip(target, tooltipText);
    }
  };

  markPointsOfContact();

  container.removeEventListener('mouseover', handleTooltipHover, true);
  container.addEventListener('mouseover', handleTooltipHover, true);
  container.removeEventListener('focusin', handleTooltipHover, true);
  container.addEventListener('focusin', handleTooltipHover, true);

  // Cleanup function
  return () => {
    container.removeEventListener('mouseover', handleTooltipHover, true);
    container.removeEventListener('focusin', handleTooltipHover, true);
    teardownActiveTooltip();
  };
};
